// Metrical-level normalization + user ×2/÷2 override. Ported verbatim from poc/web/app.js.
// Pure: arrays in, arrays out.

export interface BeatGridResult {
  beats: number[];
  downbeats: number[];
}

// Beat trackers sometimes switch metrical level mid-song (8ths in busy sections, quarters
// elsewhere). Unify to ONE pulse: estimate the true beat period, de-densify too-dense sections,
// fill too-sparse ones. Single-level songs return unchanged.
export function normalizeBeatLevel(beats: number[], downbeats: number[]): BeatGridResult {
  if (!beats || beats.length < 8) return { beats, downbeats };
  const iv: number[] = [];
  for (let i = 1; i < beats.length; i++) {
    const d = beats[i] - beats[i - 1];
    if (d > 0.12 && d < 2.0) iv.push(d);
  }
  if (iv.length < 4) return { beats, downbeats };
  const bins: Record<string, number> = {};
  iv.forEach((x) => {
    const k = (Math.round(x / 0.04) * 0.04).toFixed(2);
    bins[k] = (bins[k] || 0) + 1;
  });
  let top = Object.keys(bins).reduce((a, b) => (bins[b] > bins[a] ? b : a));
  let P = +top;
  const c2 = Object.keys(bins).find((k) => Math.abs(+k - 2 * P) < 0.1 && bins[k] >= 0.6 * bins[top]);
  if (c2) P = +c2;
  // (1) DE-DENSIFY: drop beats closer than ~0.65P
  const thr = 0.65 * P;
  const kept = [beats[0]];
  for (let i = 1; i < beats.length; i++) if (beats[i] - kept[kept.length - 1] >= thr) kept.push(beats[i]);
  // (2) FILL: a gap that is a clean integer multiple of P (≥2P) was tracked at a coarser level
  const filled = [kept[0]];
  for (let i = 1; i < kept.length; i++) {
    const gap = kept[i] - kept[i - 1];
    const n = Math.round(gap / P);
    if (n >= 2 && Math.abs(gap - n * P) < 0.35 * P)
      for (let j = 1; j < n; j++) filled.push(kept[i - 1] + (gap * j) / n);
    filled.push(kept[i]);
  }
  if (filled.length === beats.length) return { beats, downbeats };
  const ndb: number[] = [];
  (downbeats || []).forEach((t) => {
    let best = filled[0], bd = Infinity;
    for (const k of filled) { const dd = Math.abs(k - t); if (dd < bd) { bd = dd; best = k; } }
    if (!ndb.length || Math.abs(ndb[ndb.length - 1] - best) > 1e-3) ndb.push(best);
  });
  return { beats: filled, downbeats: ndb.length ? ndb : [filled[0]] };
}

export function subdivideBeats(b: number[]): number[] {
  const o: number[] = [];
  for (let i = 0; i < b.length - 1; i++) { o.push(b[i], (b[i] + b[i + 1]) / 2); }
  if (b.length) o.push(b[b.length - 1]);
  return o;
}

// user metrical-level override: ×2 counts the faster pulse, ÷2 the slower; re-derive 4-beat bars.
export function applyBeatLevel(beats: number[], downbeats: number[], mul: number): BeatGridResult {
  if (mul === 1 || !beats || beats.length < 4) return { beats, downbeats };
  let b = beats.slice(), m = mul;
  while (m >= 2) { b = subdivideBeats(b); m /= 2; }
  while (m <= 0.5) { b = b.filter((_, i) => i % 2 === 0); m *= 2; }
  const anchor = (downbeats && downbeats.length) ? downbeats[0] : b[0];
  let a0 = 0, bd = Infinity;
  for (let i = 0; i < b.length; i++) { const dd = Math.abs(b[i] - anchor); if (dd < bd) { bd = dd; a0 = i; } }
  const ndb: number[] = [];
  for (let i = a0; i < b.length; i += 4) ndb.push(b[i]);
  return { beats: b, downbeats: ndb.length ? ndb : [b[0]] };
}
